/* eslint-disable @typescript-eslint/no-explicit-any */
import { X } from "lucide-react";
import { IPortfolio } from "src/components/portfolio/PortfolioTable";
import { Dispatch, memo, SetStateAction, useState } from "react";
import { fetchWrapper, Watchlist } from "src/helpers";
import { alertService } from "src/services";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";

const MAX_QUANTITY = 10000000;
const MIN_QUANTITY = 1;
const MAX_PRICE = 10000000;
const MIN_PRICE = 0.01;
const MAX_NOTES_LENGTH = 200;

interface EditPageProps {
  getEditdata: IPortfolio | Watchlist;
  setEditingStock: Dispatch<SetStateAction<boolean>>;
  selectedDate: Date | null;
  setSelectedDate: Dispatch<SetStateAction<Date | null>>;
  getData: () => Promise<void>;
  isWatchlist?: boolean;
  showDate?: boolean;
}

const EditPageElement = (props: EditPageProps) => {
  const { getEditdata, setEditingStock, getData, isWatchlist, selectedDate } =
    props;

  const [formData, setFormData] = useState({
    quantity:
      "quantity" in getEditdata ? getEditdata.quantity?.toString() || "1" : "1",
    buy_price:
      "buy_price" in getEditdata ? getEditdata.buy_price?.toString() || "" : "",
    notes: "notes" in getEditdata ? getEditdata.notes || "" : "",
    ref_buy_id:
      "ref_buy_id" in getEditdata ? (getEditdata as any).ref_buy_id : undefined,
  });

  const [watchlistFormData, setWatchlistFormData] = useState({
    frequency:
      "is_recurring" in getEditdata && getEditdata.is_recurring !== null
        ? getEditdata.is_recurring
        : null, // Set to null instead of false when not present
    above_price:
      "above_price" in getEditdata
        ? getEditdata.above_price?.toString() || ""
        : "",
    above_percentage:
      "above_percentage" in getEditdata
        ? getEditdata.above_percentage?.toString() || ""
        : "",
    below_price:
      "below_price" in getEditdata
        ? getEditdata.below_price?.toString() || ""
        : "",
    below_percentage:
      "below_percentage" in getEditdata
        ? getEditdata.below_percentage?.toString() || ""
        : "",
  });

  const user = getLocalStorageUser() || {};
  const [errors, setErrors] = useState<{
    above?: string;
    below?: string;
    quantity?: string;
    buy_price?: string;
    notes?: string;
  }>({});
  const token = user?.token;

  const validateQuantity = (value: string): string | undefined => {
    if (!value) return "Quantity is required";
    const num = Number(value);
    if (isNaN(num)) return "Quantity must be a number";
    if (num < MIN_QUANTITY) return `Quantity must be at least ${MIN_QUANTITY}`;
    if (num > MAX_QUANTITY)
      return `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`;
    return undefined;
  };

  const validatePrice = (
    value: string,
    fieldName: string,
  ): string | undefined => {
    if (!value) return `${fieldName} is required`;
    const num = Number(value);
    if (isNaN(num)) return `${fieldName} must be a number`;
    if (num < MIN_PRICE) return `${fieldName} must be at least ₹${MIN_PRICE}`;
    if (num > MAX_PRICE)
      return `${fieldName} cannot exceed ₹${MAX_PRICE.toLocaleString()}`;
    if (!/^\d+(\.\d{1,2})?$/.test(value))
      return `${fieldName} must have at most 2 decimal places`;
    return undefined;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    let errorMessage: string | undefined;
    if (name === "notes") {
      if (value.length > MAX_NOTES_LENGTH) {
        errorMessage = `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`;
      }
      setErrors((prev) => ({ ...prev, [name]: errorMessage }));
      if (!errorMessage) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
      return;
    }

    if (name === "quantity") {
      if (value === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
        return;
      }

      if (!/^\d+$/.test(value)) {
        return;
      }

      const qty = Number(value);
      if (!isNaN(qty)) {
        if (qty > MAX_QUANTITY) {
          errorMessage = `Quantity cannot exceed ${MAX_QUANTITY.toLocaleString()}`;
        }
      }
    }

    if (name === "buy_price") {
      if (value === "") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
        return;
      }

      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }

      const price = Number(value);
      if (!isNaN(price) && price > 0) {
        if (price > MAX_PRICE) {
          errorMessage = `Price cannot exceed ₹${MAX_PRICE.toLocaleString()}`;
        }
      }
    }

    setErrors((prev) => ({ ...prev, [name]: errorMessage }));

    if (!errorMessage) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const calculatePercentage = (price: number, base: number) => {
    return ((price - base) / base) * 100;
  };

  const calculatePrice = (percentage: number, base: number) => {
    return base + (base * percentage) / 100;
  };

  const handleWatchlistInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string,
  ) => {
    const { value } = e.target;

    if (value === "") {
      setWatchlistFormData((prev) => {
        const updated = { ...prev, [field]: value };

        if (field === "above_price") {
          updated.above_percentage = "";
        } else if (field === "above_percentage") {
          updated.above_price = "";
        } else if (field === "below_price") {
          updated.below_percentage = "";
        } else if (field === "below_percentage") {
          updated.below_price = "";
        }

        return updated;
      });
      setErrors((prev) => ({ ...prev, [field]: undefined }));
      return;
    }

    if (field.includes("percentage")) {
      // Allow optional minus sign for percentage fields
      if (!/^-?\d*\.?\d{0,2}$/.test(value)) {
        return;
      }
    } else if (field.includes("price")) {
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }
    }

    const basePrice = "ltp" in getEditdata ? Number(getEditdata.ltp) : null;

    setWatchlistFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (basePrice && value !== "") {
        if (field === "above_price") {
          const price = Number(value);
          if (!isNaN(price)) {
            updated.above_percentage = calculatePercentage(
              price,
              basePrice,
            ).toFixed(2);
          }
        }
        if (field === "above_percentage") {
          const perc = Number(value);
          if (!isNaN(perc)) {
            updated.above_price = calculatePrice(perc, basePrice).toFixed(2);
          }
        }
        if (field === "below_price") {
          const price = Number(value);
          if (!isNaN(price)) {
            updated.below_percentage = calculatePercentage(
              price,
              basePrice,
            ).toFixed(2);
          }
        }
        if (field === "below_percentage") {
          const perc = Number(value);
          if (!isNaN(perc)) {
            updated.below_price = calculatePrice(perc, basePrice).toFixed(2);
          }
        }
      }

      return updated;
    });

    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: typeof errors = {};

    if (!isWatchlist) {
      const quantityError = validateQuantity(formData.quantity);
      const priceError = validatePrice(formData.buy_price, "Buy price");

      if (quantityError) newErrors.quantity = quantityError;
      if (priceError) newErrors.buy_price = priceError;

      if (formData.notes && formData.notes.length > MAX_NOTES_LENGTH) {
        newErrors.notes = `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`;
      }
    } else {
      const basePrice = "ltp" in getEditdata && getEditdata?.ltp;

      if (watchlistFormData.above_price) {
        const abovePrice = Number(watchlistFormData.above_price);
        const abovePercentage = Number(watchlistFormData.above_percentage);

        if (basePrice && abovePrice <= Number(basePrice)) {
          newErrors.above = `Above Price must be greater than ₹${basePrice}`;
        }

        if (
          isNaN(abovePercentage) ||
          abovePercentage <= 0 ||
          abovePercentage > 1000
        ) {
          newErrors.above = "Above % must be between 1% and 1000%";
        }
      }

      if (watchlistFormData.below_price) {
        const belowPrice = Number(watchlistFormData.below_price);
        const belowPercentage = Number(watchlistFormData.below_percentage);

        if (belowPrice < MIN_PRICE) {
          newErrors.below = `Below Price must be at least ₹${MIN_PRICE}`;
        } else if (basePrice && belowPrice >= Number(basePrice)) {
          newErrors.below = `Below Price must be less than ₹${basePrice}`;
        }

        if (
          isNaN(belowPercentage) ||
          belowPercentage >= 0 ||
          belowPercentage < -1000
        ) {
          newErrors.below = "Below % must be between -1% and -1000%";
        }
      }

      if (formData.notes && formData.notes.length > MAX_NOTES_LENGTH) {
        newErrors.notes = `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      let body: Record<string, any>;

      if (isWatchlist) {
        body = {
          is_recurring: watchlistFormData.frequency,
          above_price: watchlistFormData.above_price
            ? Number(watchlistFormData.above_price)
            : null,
          below_price: watchlistFormData.below_price
            ? Number(watchlistFormData.below_price)
            : null,
          current_price:
            "ltp" in getEditdata
              ? Number(getEditdata.ltp) || undefined
              : undefined,
          notes: formData.notes,
        };
      } else {
        body = {
          date: selectedDate ? selectedDate.toISOString() : "",
          quantity: Number(formData.quantity),
          buy_price: Number(formData.buy_price),
          notes: formData.notes,
          ref_buy_id: formData.ref_buy_id,
        };
      }

      const id = "id" in getEditdata && getEditdata?.id;
      await fetchWrapper.put(
        `/api/users/${isWatchlist ? "watchlists" : "portfolio"}/${id}`,
        body,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      alertService.success(`${getEditdata.symbol} updated successfully!`);
      setEditingStock(false);
      getData();
    } catch (err: any) {
      // The error message is already extracted by fetchWrapper
      const errorMessage =
        typeof err === "string" ? err : err?.message || "Failed to update.";
      alertService.error(errorMessage);
      console.error(err);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#1E1E2D] shadow-2xl rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Edit {getEditdata.symbol}
          </h3>
          <button
            onClick={() => setEditingStock(false)}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {isWatchlist ? (
            <div>
              <div className="grid grid-cols-2 gap-4">
                {/* Above Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Above Price (₹)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={watchlistFormData.above_price}
                    onChange={(e) =>
                      handleWatchlistInputChange(e, "above_price")
                    }
                    className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                    placeholder="Enter price"
                  />
                  {errors.above && (
                    <p className="text-red-500 text-sm mt-1">{errors.above}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Above Price (%)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={watchlistFormData.above_percentage}
                    onChange={(e) =>
                      handleWatchlistInputChange(e, "above_percentage")
                    }
                    className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                    placeholder="Enter percentage"
                  />
                </div>

                {/* Below Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Below Price (₹)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={watchlistFormData.below_price}
                    onChange={(e) =>
                      handleWatchlistInputChange(e, "below_price")
                    }
                    className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                    placeholder="Enter price"
                  />
                  {errors.below && (
                    <p className="text-red-500 text-sm mt-1">{errors.below}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Below Price (%)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={watchlistFormData.below_percentage}
                    onChange={(e) =>
                      handleWatchlistInputChange(e, "below_percentage")
                    }
                    className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                    placeholder="Enter percentage"
                  />
                </div>
              </div>

              <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Price Alert (Optional)
              </label>

              <div className="flex items-center gap-4 my-4">
                {["everytime", "once"].map((freq) => {
                  const canEnableAlerts =
                    (watchlistFormData.above_price &&
                      watchlistFormData.above_price !== "") ||
                    (watchlistFormData.below_price &&
                      watchlistFormData.below_price !== "");

                  const isDisabled = !canEnableAlerts;
                  console.log("watchlistFormData", watchlistFormData);
                  const isChecked =
                    watchlistFormData.frequency !== null &&
                    watchlistFormData.frequency !== undefined &&
                    ((freq === "everytime" &&
                      watchlistFormData.frequency === true) ||
                      (freq === "once" &&
                        watchlistFormData.frequency === false));

                  return (
                    <div key={freq} className="flex items-center gap-2">
                      <input
                        type="radio"
                        id={freq}
                        name="frequency"
                        value={freq}
                        checked={isChecked}
                        onChange={(e) =>
                          setWatchlistFormData({
                            ...watchlistFormData,
                            frequency: e.target.value === "everytime",
                          })
                        }
                        disabled={isDisabled}
                        className={`h-4 w-4 border-2 appearance-none rounded-full relative 
              before:content-[''] before:absolute before:top-1/2 before:left-1/2 
              before:transform before:-translate-x-1/2 before:-translate-y-1/2 
              before:w-2 before:h-2 before:rounded-full before:opacity-0 
              ${
                isDisabled
                  ? "border-gray-300 bg-gray-100 cursor-not-allowed"
                  : "border-gray-300 focus:ring-[#00A85F] cursor-pointer checked:border-[#00A85F] before:bg-[#00A85F] checked:before:opacity-100"
              }
            `}
                      />
                      <label
                        htmlFor={freq}
                        className={`text-sm font-medium ${
                          isDisabled
                            ? "text-gray-400 dark:text-gray-600 cursor-not-allowed"
                            : "text-gray-700 dark:text-gray-300 cursor-pointer"
                        }`}
                      >
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Qty *
                </label>
                <input
                  type="text"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                />
                {errors.quantity && (
                  <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price (₹) *
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="buy_price"
                  value={formData.buy_price}
                  onChange={handleInputChange}
                  className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree"
                  required
                />
                {errors.buy_price && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.buy_price}
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes (Optional) - {formData.notes.length}/{MAX_NOTES_LENGTH}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Investment thesis, target price, stop loss, etc..."
              rows={3}
              maxLength={MAX_NOTES_LENGTH}
              className="w-full pl-4 pr-4 py-2 bg-[#edf1fc] dark:bg-black border border-[#a0a5bb] rounded-md focus:ring-2 focus:ring-green-500 dark:text-white text-sm font-figtree resize-none"
            ></textarea>
            {errors.notes && (
              <p className="text-red-500 text-sm mt-1">{errors.notes}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setEditingStock(false)}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#2D2D3A] border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isWatchlist &&
                (watchlistFormData.above_price !== "" ||
                  watchlistFormData.below_price !== "") &&
                watchlistFormData.frequency === null
              }
              className={`px-4 py-2.5 text-sm font-medium text-white rounded-md ${
                isWatchlist &&
                (watchlistFormData.above_price !== "" ||
                  watchlistFormData.below_price !== "") &&
                watchlistFormData.frequency === null
                  ? "bg-gray-400 cursor-not-allowed opacity-50"
                  : "bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] hover:opacity-90 cursor-pointer"
              }`}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default memo(EditPageElement);
